import { useReduxState } from 'hooks';
import { getCourseLanguageVersionOptions } from 'lib/courses';
import { FlagIcon } from 'components/common';
import { Dropdown, DropdownButton } from 'components/common/dropdown';

import type { ButtonCategory, ButtonSize } from 'components/common/Button';

interface Props {
  category?: ButtonCategory;
  currentLanguage: string;
  includeAllOption?: boolean;
  label?: string;
  languageOptions: string[];
  onChange: (languageKey: string) => void;
  renderInPortal?: boolean;
  tooltip?: string;
  size?: ButtonSize;
  style?: React.CSSProperties;
}

const CoursesLanguageDropdown = ({
  category,
  currentLanguage,
  includeAllOption,
  label,
  languageOptions,
  onChange,
  renderInPortal,
  tooltip,
  size = 'header',
  style
}: Props) => {
  const { messages, theme } = useReduxState();

  const languageOptionsMap = getCourseLanguageVersionOptions({
    languageKeys: languageOptions
  });

  const currentLanguageObject = languageOptionsMap[currentLanguage];

  const dropdownTitle =
    currentLanguage === 'all' ? (
      messages.courseLanguageVersionsAll
    ) : currentLanguageObject ? (
      <>
        <FlagIcon
          lang={currentLanguage}
          size="small"
          style={{ marginRight: 6, marginTop: -3 }}
        />
        {currentLanguageObject.capitalizedName}
      </>
    ) : (
      currentLanguage
    );

  return (
    <div style={style}>
      {label ? <span className="label">{label}</span> : null}

      <Dropdown
        renderInPortal={renderInPortal}
        size={size}
        showArrow={true}
        styleContentButtons={false}
        title={dropdownTitle}
        tooltip={tooltip}
        {...(label
          ? {
              arrowStyle: { marginLeft: 2 },
              buttonStyle: {
                backgroundColor: 'transparent',
                color: theme.textColorLighter,
                fontSize: '1.4rem',
                padding: 0,
                position: 'relative',
                left: 6
              },
              category: 'transparent',
              underlineOnHover: true
            }
          : { category: category || 'transparent' })}
      >
        {includeAllOption && (
          <DropdownButton
            key="all"
            isSelected={currentLanguage === 'all'}
            onClick={() => onChange('all')}
            text={messages.courseLanguageVersionsAll}
          />
        )}

        {languageOptions.map((languageKey) => {
          const languageObject = languageOptionsMap[languageKey];

          return (
            <DropdownButton
              flag={languageKey}
              isSelected={currentLanguage === languageKey}
              key={languageKey}
              onClick={() => onChange(languageKey)}
              text={languageObject?.capitalizedName || languageKey}
            />
          );
        })}
      </Dropdown>

      <style jsx>{`
        .label {
          color: ${theme.textColor};
          font-size: 1.4rem;
        }
      `}</style>
    </div>
  );
};

export default CoursesLanguageDropdown;
